import { UUID } from "io-ts-types/UUID";

import { PersonalUserResponse } from "@every.org/common/src/codecs/entities";
import {
  AdminLevel,
  atLeastAsPrivilegedAs,
  FundraiserType,
  NonprofitAdminStatus,
} from "@every.org/common/src/entity/types";
import { FundraiserResponse } from "@every.org/common/src/routes/fundraiser";

export function isFundraiserAdmin(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  const isMultiNonprofitFundraiser =
    fundraiser.type === FundraiserType.MULTIPLE_NONPROFITS;

  const adminForIds = getNonprofitIdsWhereUserIsAdmin(
    isMultiNonprofitFundraiser
      ? fundraiser.forNonprofitIds ?? []
      : [fundraiser.nonprofitId],
    loggedInUser
  );

  const isSuperAdmin = atLeastAsPrivilegedAs({
    level: loggedInUser?.adminLevelForDisplay,
    compareAgainst: AdminLevel.SUPER_ADMIN,
  });

  return !!(
    isSuperAdmin ||
    (fundraiser.creatorNonprofitId &&
      adminForIds.includes(fundraiser.creatorNonprofitId))
  );
}

export function canEditOrEndFundraiser(
  fundraiser: FundraiserResponse["fundraiser"],
  loggedInUser: PersonalUserResponse | undefined
): boolean {
  const isCreator =
    loggedInUser && loggedInUser.id === fundraiser.creatorUserId;

  return !!(isCreator || isFundraiserAdmin(fundraiser, loggedInUser));
}

/*
 * Returns a list of nonprofit IDs where the user has CONFIRMED admin status.
 */
export function getNonprofitIdsWhereUserIsAdmin(
  nonprofitIds: UUID[] | string[],
  loggedInUser: PersonalUserResponse | undefined
): UUID[] {
  if (!nonprofitIds?.length || !loggedInUser) {
    return [];
  }

  const nonprofitUUIDs = nonprofitIds.filter(
    (id): id is UUID => UUID.decode(id)._tag === "Right"
  );

  const isSuperAdmin = atLeastAsPrivilegedAs({
    level: loggedInUser.adminLevelForDisplay,
    compareAgainst: AdminLevel.SUPER_ADMIN,
  });

  if (isSuperAdmin) {
    return nonprofitUUIDs.filter((id): id is UUID => UUID.is(id));
  }

  const adminFor = loggedInUser?.adminFor ?? [];
  const confirmedAdminIds = new Set(
    adminFor
      .filter((item) => item.status === NonprofitAdminStatus.CONFIRMED)
      .map((item) => item.nonprofit.id)
  );

  return nonprofitUUIDs.filter(
    (id): id is UUID => UUID.is(id) && confirmedAdminIds.has(id)
  );
}
